import { useCallback, useEffect, useState } from 'react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import Quill from 'quill';
import { MentionBlot, Mention } from 'quill-mention';
import 'quill-mention/dist/quill.mention.css';
import { Tooltip } from '@mui/material';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { User } from '../../../store/User';

Quill.register('blots/mention', MentionBlot);
Quill.register('modules/mention', Mention);

export type AtMentionData = {
  id: string;
  value: string;
};
export interface TextEditorProps {
  commentText: string;
  handleCancel: () => void;
  handleSend: (commentText: string) => void;
  placeholder: string;
  portalUsers?: User[];
  readonly?: boolean;
}

export function TextEditor({
  commentText,
  handleCancel,
  handleSend,
  placeholder,
  portalUsers = [],
  readonly = false
}: TextEditorProps) {
  const [comment, setComment] = useState<string>(commentText || '');

  const sourceFunction = (searchTerm: string, renderList: (vals: AtMentionData[], search: string) => void) => {
    const values = portalUsers.map(
      (user) => ({ id: user.email, value: `${user.firstName} ${user.lastName}` }) as AtMentionData
    );

    if (searchTerm.length === 0) {
      renderList(values, searchTerm);
    } else {
      const matches = values.filter((val) => val.value.toLowerCase().includes(searchTerm.toLowerCase()));
      renderList(matches, searchTerm);
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'indent',
    'link',
    'image',
    'video',
    'mention',
    'code-block'
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic'],
      ['link'],
      [{ list: 'bullet' }, { list: 'ordered' }],
      ['code-block']
    ],
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      mentionDenotationChars: ['@'],
      source: useCallback(sourceFunction, [portalUsers]),
      mentionContainerClass:
        'border-2 rounded bg-grey-0 min-w-[25%] max-w-[50%] max-h-[25vh] bg-white z-40 overflow-auto',
      listItemClass: 'px-2 py-2 h-full w-full text-sm text-indigo cursor-pointer text-ellipsis'
    }
  };

  const { quill, quillRef } = useQuill({ theme: 'snow', modules, formats, placeholder, readOnly: readonly });

  useEffect(() => {
    if (quill) {
      quill.root.innerHTML = commentText || '';
      quill.on('text-change', () => {
        const currentHtml = quill.root.innerHTML;
        const isContentEmpty = currentHtml === '<p><br></p>' || currentHtml.trim() === '';
        setComment(isContentEmpty ? '' : currentHtml);
      });

      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
          event.preventDefault();
          if (!isSendButtonDisabled()) handleSend(quill.root.innerHTML);
        }
      };

      quill.root.addEventListener('keydown', handleKeyDown);

      return () => {
        quill.off('text-change');
        quill.root.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [quill, commentText, handleSend]);

  const isSendButtonDisabled = () => {
    return !quill || quill.getText().trim().length === 0;
  };

  return (
    <div data-testid="quillcontainer">
      <div ref={quillRef} />
      <div className="flex mt-auto justify-end gap-5 pt-4 pb-4 self-end w-full">
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={() => handleCancel()}>
          Cancel
        </button>
        <Tooltip title="Ctrl + Enter">
          <button
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            onClick={() => handleSend(comment)}
            disabled={isSendButtonDisabled()}>
            Send
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
